import React from 'react';
import { Link } from 'react-router-dom';
import LegalLayout, { LegalSection } from '../components/LegalLayout';
import { legalInfo, legalDates } from '../lib/legal';

const Privacidad = () => (
  <LegalLayout
    title="Política de Privacidad"
    seoTitle="Política de Privacidad | Brazolovers"
    seoDescription="Cómo Brazolovers trata tus datos personales conforme a la Ley Orgánica de Protección de Datos Personales del Ecuador: qué recopilamos, para qué, con quién lo compartimos y cómo ejercer tus derechos."
    path="/privacidad/"
    updatedAt={legalDates.privacidad}
    intro="En Brazolovers tratamos tus datos personales solo para lo que hace falta: recibir tu pedido, entregártelo y responderte. Esta política explica qué recopilamos, por qué, y cómo puedes controlarlo."
  >
    <LegalSection id="responsable" heading="1. Quién es el responsable">
      <p>
        El responsable del tratamiento de tus datos personales es{' '}
        <strong>{legalInfo.razonSocial}</strong>, {legalInfo.tipoPersona} que opera bajo el nombre
        comercial <strong>{legalInfo.nombreComercial}</strong>, con RUC {legalInfo.ruc} y domicilio
        en {legalInfo.domicilioFiscal}.
      </p>
      <p>
        Para cualquier asunto relacionado con tus datos personales puedes escribirnos a{' '}
        <a href={`mailto:${legalInfo.emailDatos}`} className="text-caramel font-medium hover:underline">
          {legalInfo.emailDatos}
        </a>.
      </p>
    </LegalSection>

    <LegalSection id="marco" heading="2. Marco legal aplicable">
      <p>
        Esta política se rige por la <strong>Ley Orgánica de Protección de Datos Personales
        (LOPDP)</strong> del Ecuador y su Reglamento, así como por la Ley Orgánica de Defensa del
        Consumidor y la Ley de Comercio Electrónico, Firmas Electrónicas y Mensajes de Datos.
      </p>
    </LegalSection>

    <LegalSection id="datos" heading="3. Qué datos recopilamos">
      <p>Solo recopilamos lo necesario según cómo interactúes con nosotros:</p>
      <ul className="list-disc list-outside pl-5 space-y-2">
        <li>
          <strong>Si haces un pedido:</strong> tu nombre, número de teléfono (WhatsApp), el local
          de retiro o la dirección de entrega, la fecha y hora que agendas, el detalle de tu
          pedido y los comentarios que nos escribas.
        </li>
        <li>
          <strong>Si pides entrega a domicilio:</strong> la ubicación de entrega que nos indicas.
          La usamos para calcular la distancia y el costo del domicilio, y para que el repartidor
          llegue.
        </li>
        <li>
          <strong>Si nos escribes:</strong> por WhatsApp, correo o redes sociales, los datos que
          incluyas en tu mensaje.
        </li>
        <li>
          <strong>Datos de navegación:</strong> páginas visitadas, tipo de dispositivo y datos
          estadísticos agregados a través de Google Analytics (ver la sección de cookies).
        </li>
      </ul>
      <p>
        <strong>No pedimos ni almacenamos datos de tarjetas de crédito o débito en este sitio.</strong>{' '}
        Tampoco recopilamos datos sensibles (salud, origen étnico, religión, etc.) ni datos de
        menores de edad de forma deliberada.
      </p>
    </LegalSection>

    <LegalSection id="finalidades" heading="4. Para qué usamos tus datos">
      <ul className="list-disc list-outside pl-5 space-y-2">
        <li>Preparar, cobrar y entregar tu pedido, y coordinar contigo si algo cambia.</li>
        <li>Emitir el comprobante de venta que exige la normativa tributaria ecuatoriana.</li>
        <li>Responder tus consultas, reclamos o solicitudes de cotización.</li>
        <li>Entender de forma agregada cómo se usa el sitio para mejorarlo.</li>
        <li>Cumplir obligaciones legales, contables y tributarias.</li>
      </ul>
      <p>
        No usamos tus datos para publicidad de terceros ni los vendemos. Si alguna vez quisiéramos
        enviarte promociones, te pediremos tu consentimiento por separado y podrás retirarlo cuando
        quieras.
      </p>
    </LegalSection>

    <LegalSection id="base" heading="5. Base legal del tratamiento">
      <ul className="list-disc list-outside pl-5 space-y-2">
        <li>
          <strong>Ejecución del contrato:</strong> los datos de tu pedido y entrega los tratamos
          porque son indispensables para cumplir con la compra que nos solicitas.
        </li>
        <li>
          <strong>Cumplimiento de una obligación legal:</strong> facturación y conservación de
          respaldos contables y tributarios.
        </li>
        <li>
          <strong>Tu consentimiento:</strong> para cookies y tecnologías de medición no esenciales,
          y para cualquier comunicación comercial.
        </li>
        <li>
          <strong>Interés legítimo:</strong> mantener la seguridad del sitio y prevenir fraudes o
          pedidos falsos.
        </li>
      </ul>
    </LegalSection>

    <LegalSection id="terceros" heading="6. Con quién compartimos tus datos">
      <p>
        Compartimos únicamente lo necesario y solo con quienes nos ayudan a prestarte el servicio:
      </p>
      <ul className="list-disc list-outside pl-5 space-y-2">
        <li>
          <strong>Repartidores y plataformas de entrega</strong> (por ejemplo Uber Eats), cuando tu
          pedido llega por ese canal: nombre, teléfono y dirección de entrega.
        </li>
        <li>
          <strong>Google</strong> (Analytics y servicios de mapas y cálculo de distancias), como
          proveedor tecnológico.
        </li>
        <li><strong>Meta / WhatsApp</strong>, cuando eliges comunicarte con nosotros por ese canal.</li>
        <li>
          <strong>Autoridades competentes</strong> (SRI, entidades de control, autoridad judicial),
          cuando la ley lo exige.
        </li>
      </ul>
      <p>
        Algunos de estos proveedores procesan datos fuera del Ecuador. En esos casos se trata de
        transferencias internacionales amparadas en la ejecución del contrato contigo y en las
        garantías contractuales que estos proveedores ofrecen.
      </p>
    </LegalSection>

    <LegalSection id="conservacion" heading="7. Cuánto tiempo los conservamos">
      <ul className="list-disc list-outside pl-5 space-y-2">
        <li>
          <strong>Datos del pedido:</strong> mientras dure la relación y luego durante los plazos de
          conservación tributaria y contable exigidos por la ley ecuatoriana (7 años).
        </li>
        <li><strong>Mensajes y consultas:</strong> hasta 24 meses desde el último contacto.</li>
        <li><strong>Datos analíticos:</strong> según la configuración de retención de Google Analytics.</li>
      </ul>
      <p>Cumplido el plazo, los datos se eliminan o se anonimizan.</p>
    </LegalSection>

    <LegalSection id="derechos" heading="8. Tus derechos">
      <p>
        La LOPDP te reconoce, sin costo, los derechos de <strong>acceso</strong>,{' '}
        <strong>rectificación y actualización</strong>, <strong>eliminación</strong>,{' '}
        <strong>oposición</strong>, <strong>anulación</strong>, <strong>portabilidad</strong>,{' '}
        <strong>suspensión del tratamiento</strong> y a{' '}
        <strong>no ser objeto de decisiones automatizadas</strong> que te afecten
        significativamente.
      </p>
      <p>
        Para ejercerlos, escríbenos a{' '}
        <a href={`mailto:${legalInfo.emailDatos}`} className="text-caramel font-medium hover:underline">
          {legalInfo.emailDatos}
        </a>{' '}
        indicando tu nombre, el derecho que quieres ejercer y un dato que nos permita ubicar tu
        pedido (por ejemplo, el teléfono con el que compraste). Responderemos dentro de los plazos
        que establece la ley.
      </p>
      <p>
        Si consideras que no atendimos tu solicitud, puedes presentar un reclamo ante la{' '}
        <strong>Superintendencia de Protección de Datos Personales del Ecuador</strong>.
      </p>
    </LegalSection>

    <LegalSection id="cookies" heading="9. Cookies y tecnologías similares">
      <p>Este sitio usa:</p>
      <ul className="list-disc list-outside pl-5 space-y-2">
        <li>
          <strong>Almacenamiento técnico esencial:</strong> guardamos tu carrito en el
          almacenamiento local de tu navegador para que no pierdas lo que ya elegiste. No sale de
          tu dispositivo hasta que confirmas el pedido.
        </li>
        <li>
          <strong>Cookies analíticas (Google Analytics 4):</strong> nos dicen de forma agregada qué
          páginas se visitan y cómo llegan los usuarios. No las usamos para identificarte.
        </li>
      </ul>
      <p>
        Puedes borrar o bloquear las cookies desde la configuración de tu navegador. Si bloqueas
        las analíticas, el sitio sigue funcionando igual.
      </p>
    </LegalSection>

    <LegalSection id="seguridad" heading="10. Seguridad">
      <p>
        Aplicamos medidas técnicas y organizativas razonables para proteger tus datos: conexión
        cifrada (HTTPS), acceso restringido a los sistemas internos y respaldos. Ningún sistema es
        infalible; si ocurriera una vulneración que afecte tus derechos, te lo notificaremos y lo
        comunicaremos a la autoridad conforme a la LOPDP.
      </p>
    </LegalSection>

    <LegalSection id="cambios" heading="11. Cambios a esta política">
      <p>
        Podemos actualizar esta política cuando cambien nuestros servicios o la normativa. La
        versión vigente es siempre la publicada en esta página, con su fecha de última actualización
        al inicio.
      </p>
    </LegalSection>

    <LegalSection id="contacto" heading="12. Contacto">
      <p>
        ¿Dudas sobre esta política? Escríbenos a{' '}
        <a href={`mailto:${legalInfo.email}`} className="text-caramel font-medium hover:underline">
          {legalInfo.email}
        </a>{' '}
        o por los canales de la página de{' '}
        <Link to="/contacto/" className="text-caramel font-medium hover:underline">contacto</Link>.
      </p>
    </LegalSection>
  </LegalLayout>
);

export default Privacidad;
